import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Page } from './Page';
import { MdOutlinePrivacyTip } from 'react-icons/md';

export const Terms: React.FC = () => {
  const { t } = useTranslation();
  const [activeSection, setActiveSection] = useState('agreement');

  const sections = [
    { id: 'agreement', title: t('termsOfUse.sections.agreement.title') },
    { id: 'disclaimer', title: t('termsOfUse.sections.disclaimer.title') },
    { id: 'age', title: t('termsOfUse.sections.age.title') },
    { id: 'intellectual', title: t('termsOfUse.sections.intellectual.title') },
    { id: 'liability', title: t('termsOfUse.sections.liability.title') },
    { id: 'changes', title: t('termsOfUse.sections.changes.title') },
    { id: 'contact', title: t('termsOfUse.sections.contact.title') },
  ];

  return (
    <Page>
      <div className="max-w-5xl mx-auto px-6 py-16 mt-12 text-white">
        {/* Header Section */}
        <div>
          <h1 className="text-2xl sm:text-4xl font-bold text-center mb-3 text-stroke">
            <MdOutlinePrivacyTip className="inline-block mr-4" />
            {t('termsOfUse.title')}
          </h1>
          <p className="text-gray-200">{t('termsOfUse.lastUpdated')}</p>
        </div>

        {/* Content Grid Layout */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Left Sidebar Navigation */}
          <div className="md:col-span-1 mt-10">
            <div className="bg-transparent backdrop-blur-sm border border-gray-200 rounded-lg p-4 sticky top-24">
              <h2 className="font-bold text-lg mb-4 text-white">
                {t('termsOfUse.contents')}
              </h2>
              <nav>
                <ul className="space-y-2">
                  {sections.map((section) => (
                    <li key={section.id}>
                      <button
                        onClick={() => setActiveSection(section.id)}
                        className={`w-full text-left px-3 py-2 rounded-md transition-colors ${
                          activeSection === section.id
                            ? 'bg-purple-800/30 text-white font-medium backdrop-blur-sm border border-white/20'
                            : 'hover:bg-white/10 text-white backdrop-blur-sm'
                        }`}
                      >
                        {section.title}
                      </button>
                    </li>
                  ))}
                </ul>
              </nav>
            </div>
          </div>

          {/* Main Content Area */}
          <div className="md:col-span-2">
            <div className="bg-transparent rounded-lg p-8">
              {/* Agreement to Terms */}
              <section
                id="agreement"
                className={activeSection === 'agreement' ? 'block' : 'hidden'}
              >
                <h2 className="text-2xl font-bold text-white mb-4">
                  {t('termsOfUse.sections.agreement.title')}
                </h2>
                <div className="bg-blue-50/30 backdrop-blur-sm border-l-4 border-blue-500 p-4 mb-6">
                  <p className="text-white">
                    {t('termsOfUse.sections.agreement.description')}
                  </p>
                </div>
              </section>

              {/* Disclaimer */}
              <section
                id="disclaimer"
                className={activeSection === 'disclaimer' ? 'block' : 'hidden'}
              >
                <h2 className="text-2xl font-bold text-white mb-4">
                  {t('termsOfUse.sections.disclaimer.title')}
                </h2>

                <div className="bg-purple-50/20 backdrop-blur-sm rounded-lg border border-purple-300/30 p-5 mb-6">
                  <div className="flex items-center mb-3">
                    <div className="bg-purple-500/30 p-2 rounded-full mr-3">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-6 w-6 text-white"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                        />
                      </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-white">
                      {t('termsOfUse.sections.disclaimer.importantInfo')}
                    </h3>
                  </div>

                  <div className="space-y-4">
                    <p className="text-white">
                      {t('termsOfUse.sections.disclaimer.paragraph1')}
                    </p>

                    <p className="text-white">
                      {t('termsOfUse.sections.disclaimer.paragraph2')}
                    </p>

                    <p className="text-white">
                      {t('termsOfUse.sections.disclaimer.paragraph3')}
                    </p>
                  </div>
                </div>

                <div className="bg-yellow-50/30 backdrop-blur-sm border-l-4 border-yellow-400 p-4 mt-6">
                  <h4 className="font-semibold text-yellow-200 mb-2">
                    {t('termsOfUse.sections.disclaimer.responsibleGambling')}
                  </h4>
                  <p className="text-white">
                    {t(
                      'termsOfUse.sections.disclaimer.responsibleGamblingDesc',
                    )}
                  </p>
                </div>
              </section>

              {/* Age Restriction */}
              <section
                id="age"
                className={activeSection === 'age' ? 'block' : 'hidden'}
              >
                <h2 className="text-2xl font-bold text-white mb-4">
                  {t('termsOfUse.sections.age.title')}
                </h2>

                <div className="bg-transparent backdrop-blur-sm border border-gray-200 rounded-lg p-5 mb-6">
                  <div className="flex items-center mb-4">
                    <div className="bg-red-500/30 p-2 rounded-full mr-3">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-6 w-6 text-white"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
                        />
                      </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-white">
                      {t('termsOfUse.sections.age.ageLimit')}
                    </h3>
                  </div>

                  <p className="text-white">
                    {t('termsOfUse.sections.age.description')}
                  </p>

                  <div className="mt-4 bg-red-100/30 backdrop-blur-sm border-l-4 border-red-500 p-4">
                    <p className="text-white font-semibold">
                      {t('termsOfUse.sections.age.restriction')}
                    </p>
                  </div>
                </div>
              </section>

              {/* Intellectual Property */}
              <section
                id="intellectual"
                className={
                  activeSection === 'intellectual' ? 'block' : 'hidden'
                }
              >
                <h2 className="text-2xl font-bold text-white mb-4">
                  {t('termsOfUse.sections.intellectual.title')}
                </h2>

                <div className="bg-transparent backdrop-blur-sm border border-gray-200 rounded-lg p-5 mb-6">
                  <p className="text-white mb-4">
                    {t('termsOfUse.sections.intellectual.description')}
                  </p>

                  <div className="space-y-3 mt-4">
                    <h3 className="text-lg font-semibold text-white">
                      {t('termsOfUse.sections.intellectual.prohibitedUses')}
                    </h3>
                    <div className="space-y-2">
                      <div className="flex items-start">
                        <div className="bg-red-500/30 p-1 rounded-full mr-3 mt-1">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-4 w-4 text-white"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                        </div>
                        <p className="text-white text-sm">
                          {t('termsOfUse.sections.intellectual.prohibition1')}
                        </p>
                      </div>
                      <div className="flex items-start">
                        <div className="bg-red-500/30 p-1 rounded-full mr-3 mt-1">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-4 w-4 text-white"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                        </div>
                        <p className="text-white text-sm">
                          {t('termsOfUse.sections.intellectual.prohibition2')}
                        </p>
                      </div>
                      <div className="flex items-start">
                        <div className="bg-red-500/30 p-1 rounded-full mr-3 mt-1">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-4 w-4 text-white"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                        </div>
                        <p className="text-white text-sm">
                          {t('termsOfUse.sections.intellectual.prohibition3')}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Limitation of Liability */}
              <section
                id="liability"
                className={activeSection === 'liability' ? 'block' : 'hidden'}
              >
                <h2 className="text-2xl font-bold text-white mb-4">
                  {t('termsOfUse.sections.liability.title')}
                </h2>

                <div className="bg-red-50/20 backdrop-blur-sm border-l-4 border-red-500 p-5 mb-6">
                  <p className="text-white">
                    {t('termsOfUse.sections.liability.description')}
                  </p>
                </div>

                <div className="bg-transparent backdrop-blur-sm border border-gray-200 rounded-lg p-5 mt-6">
                  <h3 className="text-lg font-semibold text-white mb-3">
                    {t('termsOfUse.sections.liability.whatThisMeans')}
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-start">
                      <div className="bg-gray-500/30 p-1 rounded-full mr-3 mt-1">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4 text-white"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                      </div>
                      <p className="text-white text-sm">
                        {t('termsOfUse.sections.liability.point1')}
                      </p>
                    </div>
                    <div className="flex items-start">
                      <div className="bg-gray-500/30 p-1 rounded-full mr-3 mt-1">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4 text-white"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                      </div>
                      <p className="text-white text-sm">
                        {t('termsOfUse.sections.liability.point2')}
                      </p>
                    </div>
                    <div className="flex items-start">
                      <div className="bg-gray-500/30 p-1 rounded-full mr-3 mt-1">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4 text-white"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                      </div>
                      <p className="text-white text-sm">
                        {t('termsOfUse.sections.liability.point3')}
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Changes to Terms */}
              <section
                id="changes"
                className={activeSection === 'changes' ? 'block' : 'hidden'}
              >
                <h2 className="text-2xl font-bold text-white mb-4">
                  {t('termsOfUse.sections.changes.title')}
                </h2>
                <p className="text-white mb-5">
                  {t('termsOfUse.sections.changes.description')}
                </p>
                <div className="flex items-center mt-6 bg-gray-50/30 backdrop-blur-sm p-4 rounded-lg border border-gray-200">
                  <div className="bg-blue-100/30 p-2 rounded-full mr-4">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-6 w-6 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <p className="text-white text-sm">
                    {t('termsOfUse.sections.changes.note')}
                  </p>
                </div>
              </section>

              {/* Contact */}
              <section
                id="contact"
                className={activeSection === 'contact' ? 'block' : 'hidden'}
              >
                <h2 className="text-2xl font-bold text-white mb-4">
                  {t('termsOfUse.sections.contact.title')}
                </h2>
                <p className="text-white mb-6">
                  {t('termsOfUse.sections.contact.description')}
                </p>
                <a
                  href="mailto:contact@betzen.app"
                  className="inline-flex items-center px-4 py-2 rounded-md hover:opacity-90 transition-opacity"
                  style={{
                    background: 'linear-gradient(90deg, #5f2c82, #49a09d)',
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 mr-2 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                  <span className="text-white">contact@betzen.app</span>
                </a>
              </section>
            </div>
          </div>
        </div>
      </div>
    </Page>
  );
};
